import { FilterSelectionState } from '@/filters/selection/FilterSelectionState';
import { SortingState } from '@/sorting/SortingState';
import { IEmbeddedStateMutable } from './IEmbeddedStateMutable';
import { IEmbeddedState } from './IEmbeddedState';
import { Degree } from '@/models/Degree';
import { QueryParametersState } from './QueryParametersState';

export class EmbeddedState implements IEmbeddedStateMutable {
	private _filterSelectionState: FilterSelectionState;
	private _queryParametersState: QueryParametersState;
	private _sortingState: SortingState;
	private _degree: Degree | null = null;
	private _page: number | null = null;

	public constructor() {
		this._filterSelectionState = new FilterSelectionState();
		this._queryParametersState = new QueryParametersState();
		this._sortingState = new SortingState();
	}

	public get filterSelectionState(): FilterSelectionState {
		return this._filterSelectionState;
	}

	public get queryParameters(): QueryParametersState {
		return this._queryParametersState;
	}

	public get sortingState(): SortingState {
		return this._sortingState;
	}

	public get degreeOrNull(): Degree | null {
		return this._degree;
	}

	public get degree(): Degree {
		if (this._degree === null) {
			throw new Error('Degree is null');
		}
		return this._degree;
	}

	public get pageOrNull(): number | null {
		return this._page;
	}

	public get page(): number {
		if (this._page === null) {
			throw new Error('Page is null');
		}
		return this._page;
	}

	public toMutable(): IEmbeddedStateMutable {
		return new EmbeddedState().mergeWith(this);
	}

	public withFilterSelection(filterSelection: FilterSelectionState): EmbeddedState {
		this._filterSelectionState = filterSelection;
		return this;
	}

	public withQueryParameters(queryParametersState: QueryParametersState): EmbeddedState {
		this._queryParametersState = queryParametersState;
		return this;
	}

	public withSortingOption(sortingState: SortingState): EmbeddedState {
		this._sortingState = sortingState;
		return this;
	}

	public withDegree(degree: Degree): EmbeddedState {
		this._degree = degree;
		return this;
	}

	public withPage(page: number): EmbeddedState {
		this._page = page;
		return this;
	}

	public mergeFilterSelectionWith(filterSelection: FilterSelectionState): EmbeddedState {
		this._filterSelectionState = this._filterSelectionState.mergeWith(filterSelection);
		return this;
	}

	public mergeWith(state: IEmbeddedState): EmbeddedState {
		this.mergeFilterSelectionWith(state.filterSelectionState);
		this._queryParametersState = state.queryParameters;
		this._sortingState = state.sortingState;
		this._degree = state.degreeOrNull;
		this._page = state.pageOrNull;

		return this;
	}

	public isEqual(other: IEmbeddedState): boolean {
		return (
			this.filterSelectionState.isEqual(other.filterSelectionState) &&
			this.queryParameters.isEqual(other.queryParameters) &&
			this.degreeOrNull?.value === other.degreeOrNull?.value &&
			this.pageOrNull === other.pageOrNull
		);
	}
}
